import { useState, FC, ReactNode } from 'react';
import { ToTop } from '../modules';

type SectionProps = {
    title: string;
    children: ReactNode;
};

const Section: FC<SectionProps> = ({ title, children }) => (
    <section style={{ marginBottom: '2.5rem' }}>
        <h2
            className="privacypolicy_contents_h2"
            style={{
                borderBottom: '2px solid #eee',
                paddingBottom: '0.5rem',
                marginBottom: '1.5rem',
            }}
        >
            {title}
        </h2>
        <div style={{ lineHeight: 1.8, color: '#555' }}>{children}</div>
    </section>
);

type PolicyHeaderProps = {
    title: string;
    subTitle: string;
    lastUpdate: string;
};

const PolicyHeader: FC<PolicyHeaderProps> = ({ title, subTitle, lastUpdate }) => (
    <header style={{ textAlign: 'center', marginBottom: '4rem' }}>
        <h1 className="privacypolicy_title" style={{ marginBottom: '0.5rem' }}>
            {title}
        </h1>
        <h2
            className="privacypolicy_subTitle"
            style={{ marginBottom: '1rem', color: '#666' }}
        >
            {subTitle}
        </h2>
        <p
            className="privacypolicy_contents_body"
            style={{ color: '#888', fontSize: '0.9rem' }}
        >
            {lastUpdate}
        </p>
    </header>
);

type LangButtonProps = {
    onClick: () => void;
    isActive: boolean;
    children: ReactNode;
    style?: React.CSSProperties;
};

const LangButton: FC<LangButtonProps> = ({ onClick, isActive, children, style }) => (
    <button
        onClick={onClick}
        style={{
            ...style,
            padding: '8px 16px',
            cursor: 'pointer',
            border: isActive ? '1px solid #333' : '1px solid #ccc',
            backgroundColor: isActive ? '#333' : 'white',
            color: isActive ? 'white' : '#333',
            borderRadius: '4px',
            transition: 'all 0.3s',
            minWidth: '110px',
            whiteSpace: 'nowrap',
            height: '40px',
        }}
    >
        {children}
    </button>
);

const FlowIntervalTermsOfService: FC = () => {
    const [lang, setLang] = useState<'jp' | 'en'>('jp');

    // 日本語コンテンツ
    const jpContent = {
        title: 'Flow Interval',
        subTitle: '利用規約',
        lastUpdate: '最終更新日: 2025年12月28日',
        section1Title: '第1条（はじめに）',
        section1Body:
            'この利用規約（以下「本規約」）は、Flow Interval アプリ（以下「本アプリ」）の利用条件を定めるものです。本アプリを利用するすべてのユーザー（以下「ユーザー」）は、本規約に同意した上で利用します。',
        section2Title: '第2条（本アプリの利用）',
        section2_1Li: '本アプリは、ユーザーが指定した時間間隔で動画を録画し、端末に保存する機能を提供します。',
        section2_2Li: 'ユーザーは自己の責任で本アプリを利用し、作成したコンテンツについて全ての責任を負います。',
        section3Title: '第3条（有料機能）',
        section3_1Li: '本アプリは、アプリ内課金により利用可能なプレミアム機能（以下「有料機能」）を提供します。',
        section3_2Li: '有料機能の購入は、Apple の決済システムを通じて行われ、Apple の利用規約が適用されます。',
        section4Title: '第4条（禁止事項）',
        section4Body: 'ユーザーは、以下の行為をしてはなりません。',
        section4_1Li: '法令または公序良俗に違反する行為。',
        section4_2Li: '犯罪行為に関連する行為。',
        section4_3Li: '本アプリのサーバーまたはネットワークの機能を破壊・妨害する行為。',
        section4_4Li: '本アプリの運営を妨害する行為。',
        section4_5Li: '有料機能を不正に利用しようとする行為。',
        section4_6Li: '本アプリの知的財産権を侵害する行為。',
        section4_7Li: '他者の著作権を侵害するコンテンツを作成・配布する行為。',
        section4_8Li: 'その他、開発者が不適切と判断する行為。',
        section5Title: '第5条（知的財産権）',
        section5Body:
            '本アプリおよびそのコンテンツに関する著作権・商標権・その他の権利は全て開発者に帰属します。ユーザーは法令で認められる私的利用の範囲を超えて複製・配布・改変・商用利用してはいけません。',
        section6Title: '第6条（免責事項）',
        section6_1Li:
            '本アプリは現状有姿で提供され、開発者は明示・黙示を問わず一切の保証を行いません。',
        section6_2Li:
            '本アプリの利用に起因してユーザーに生じた損害について、開発者は一切の責任を負いません。',
        section7Title: '第7条（規約の変更）',
        section7Body:
            '開発者は必要と判断した場合、ユーザーへの通知なく本規約を変更できます。変更後は本アプリ内に表示された時点で効力を生じます。',
        section8Title: '第8条（お問い合わせ）',
        section8Body:
            '本規約に関するお問い合わせは、以下のメールアドレスまでお願いします。\nbrownpowder@gmail.com',
    };

    // 英語コンテンツ
    const enContent = {
        title: 'Flow Interval',
        subTitle: 'Terms of Service',
        lastUpdate: 'Last Updated: December 28, 2025',
        section1Title: 'Article 1 (Introduction)',
        section1Body:
            'These Terms of Service ("Terms") set forth the terms and conditions for using the Flow Interval app ("the App"). All users of the App agree to these Terms before using the App.',
        section2Title: 'Article 2 (Use of the App)',
        section2_1Li:
            'The App provides functionality to record videos at specified time intervals and save them on the device.',
        section2_2Li:
            'Users are responsible for their use of the App and any content created with it.',
        section3Title: 'Article 3 (Paid Features)',
        section3_1Li:
            'The App offers premium features via in‑app purchases ("Paid Features").',
        section3_2Li:
            'Purchases are processed through Apple’s payment system and are subject to Apple’s terms.',
        section4Title: 'Article 4 (Prohibited Acts)',
        section4Body:
            'Users must not engage in the following acts:',
        section4_1Li: 'Acts that violate laws or public order and morals.',
        section4_2Li: 'Criminal activities.',
        section4_3Li:
            'Destruction or interference with the App’s server or network functions.',
        section4_4Li: 'Interfering with the operation of the App.',
        section4_5Li: 'Attempting to use Paid Features by unauthorized means.',
        section4_6Li: 'Infringing the App’s intellectual property rights.',
        section4_7Li: 'Creating or distributing content that infringes others’ copyrights.',
        section4_8Li: 'Other acts deemed inappropriate by the developer.',
        section5Title: 'Article 5 (Intellectual Property Rights)',
        section5Body:
            'All copyrights, trademarks, and other rights related to the App belong to the developer. Users may not reproduce, distribute, modify, or use the App commercially beyond the scope permitted by law.',
        section6Title: 'Article 6 (Disclaimer)',
        section6_1Li:
            'The App is provided "as is" without any express or implied warranties.',
        section6_2Li:
            'The developer is not liable for any damages arising from the use of the App.',
        section7Title: 'Article 7 (Changes to Terms)',
        section7Body:
            'The developer may change these Terms at any time without notice. Changes become effective when posted in the App.',
        section8Title: 'Article 8 (Contact)',
        section8Body:
            'For any questions regarding these Terms, please contact: brownpowder@gmail.com',
    };

    const currentContent = lang === 'jp' ? jpContent : enContent;

    return (
        <div className="container privacypolicy" style={{ width: '100%', minHeight: '100vh', backgroundColor: '#fff', padding: '4rem 2rem' }}>
            <ToTop />
            <div className="privacypolicy_contents" style={{ maxWidth: '800px', margin: '0 auto', fontFamily: 'sans-serif' }}>
                <div style={{ textAlign: 'right', marginBottom: '2rem' }}>
                    <LangButton onClick={() => setLang('jp')} isActive={lang === 'jp'} style={{ marginRight: '10px' }}>
                        日本語
                    </LangButton>
                    <LangButton onClick={() => setLang('en')} isActive={lang === 'en'}>
                        English
                    </LangButton>
                </div>
                <PolicyHeader title={currentContent.title} subTitle={currentContent.subTitle} lastUpdate={currentContent.lastUpdate} />
                <Section title={currentContent.section1Title}>
                    <p className="privacypolicy_contents_body" style={{ marginBottom: '2.5rem' }}>{currentContent.section1Body}</p>
                </Section>
                <Section title={currentContent.section2Title}>
                    <ul className="privacypolicy_contents_ul">
                        <li className="privacypolicy_contents_ul_li">{currentContent.section2_1Li}</li>
                        <li className="privacypolicy_contents_ul_li">{currentContent.section2_2Li}</li>
                    </ul>
                </Section>
                <Section title={currentContent.section3Title}>
                    <ul className="privacypolicy_contents_ul">
                        <li className="privacypolicy_contents_ul_li">{currentContent.section3_1Li}</li>
                        <li className="privacypolicy_contents_ul_li">{currentContent.section3_2Li}</li>
                    </ul>
                </Section>
                <Section title={currentContent.section4Title}>
                    <p className="privacypolicy_contents_body" style={{ marginBottom: '2.5rem' }}>{currentContent.section4Body}</p>
                    <ul className="privacypolicy_contents_ul">
                        <li className="privacypolicy_contents_ul_li">{currentContent.section4_1Li}</li>
                        <li className="privacypolicy_contents_ul_li">{currentContent.section4_2Li}</li>
                        <li className="privacypolicy_contents_ul_li">{currentContent.section4_3Li}</li>
                        <li className="privacypolicy_contents_ul_li">{currentContent.section4_4Li}</li>
                        <li className="privacypolicy_contents_ul_li">{currentContent.section4_5Li}</li>
                        <li className="privacypolicy_contents_ul_li">{currentContent.section4_6Li}</li>
                        <li className="privacypolicy_contents_ul_li">{currentContent.section4_7Li}</li>
                        <li className="privacypolicy_contents_ul_li">{currentContent.section4_8Li}</li>
                    </ul>
                </Section>
                <Section title={currentContent.section5Title}>
                    <p className="privacypolicy_contents_body" style={{ marginBottom: '2.5rem' }}>{currentContent.section5Body}</p>
                </Section>
                <Section title={currentContent.section6Title}>
                    <ul className="privacypolicy_contents_ul">
                        <li className="privacypolicy_contents_ul_li">{currentContent.section6_1Li}</li>
                        <li className="privacypolicy_contents_ul_li">{currentContent.section6_2Li}</li>
                    </ul>
                </Section>
                <Section title={currentContent.section7Title}>
                    <p className="privacypolicy_contents_body" style={{ marginBottom: '2.5rem' }}>{currentContent.section7Body}</p>
                </Section>
                <Section title={currentContent.section8Title}>
                    <p className="privacypolicy_contents_body" style={{ whiteSpace: 'pre-line' }}>{currentContent.section8Body}</p>
                </Section>
            </div>
        </div>
    );
};

export default FlowIntervalTermsOfService;
